/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { Alert, Form, Input, Label, Spinner } from 'reactstrap';
import PropTypes from 'prop-types';
import MaterialIcon from '@material/react-material-icon';
import { useQuery } from '@tanstack/react-query';
import { showToast } from '../../components/toast/Toast';
import useAxios from '../../hooks/useAxios';

const FormAttendanceReview = ({ togglePopup, refetch, requestType }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [clockInChecked, setClockInChecked] = useState(false);
  const [clockOutChecked, setClockOutChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitData, setSubmitData] = useState({
    clock_in: '',
    clock_out: '',
    description: '',
    attachment: null,
  });
  const [isPlanned, setIsPlanned] = useState(true);
  const [selectedUnplannedOption, setSelectedUnplannedOption] = useState(undefined);
  const [selectedUplannedMetadData, setSelectedUplannedMetadData] = useState({});

  const api = useAxios();

  const {
    data: unplannedOptions,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['unplannedOptions'],
    queryFn: () => api.get('unplanned-reason-leave').then((res) => res.data.data),
  });

  useEffect(() => {
    if (unplannedOptions && selectedUnplannedOption !== undefined) {
      setSelectedUplannedMetadData(
        unplannedOptions.find((option) => option.id === selectedUnplannedOption) || {},
      );
    }
  }, [selectedUnplannedOption]);

  const handleCheck = (e) => {
    if (e.target.name === 'clock-in-check') {
      setClockInChecked(!clockInChecked);
    } else {
      setClockOutChecked(!clockOutChecked);
    }
  };

  const handleChange = (e) => {
    setSubmitData({ ...submitData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSubmitData({ ...submitData, attachment: file });
    } else {
      setSubmitData({ ...submitData, attachment: null });
    }
  };

  //   const compareDate = (date) => {
  //     const d1 = new Date(date);
  //     const d2 = new Date();

  //     if (d1 > d2) {
  //       showToast('Tanggal absen invalid', 'error');
  //       return false;
  //     }

  //     return true;
  //   };

  const validateForm = () => {
    if (clockInChecked && clockOutChecked) {
      showToast('Pilih salah satu jenis pengajuan', 'error');
      return false;
    }

    if (!isPlanned && selectedUnplannedOption === undefined) {
      showToast('Alasan tidak terencana harus dipilih', 'error');
      return false;
    }

    if (selectedUplannedMetadData?.required_attachment && !submitData.attachment) {
      showToast('Lampiran bukti harus diisi', 'error');
      return false;
    }

    if (submitData.attachment && submitData.attachment.size > 20 * 1024 * 1024) {
      showToast('Ukuran lampiran tidak boleh lebih dari 20MB', 'error');
      return false;
    }

    if (
      submitData.attachment &&
      !['image/jpeg', 'image/png'].includes(submitData.attachment.type)
    ) {
      showToast('Format lampiran harus jpeg/jpg/png', 'error');
      return false;
    }

    if (selectedDate === '') {
      showToast('Tanggal absen harus diisi', 'error');
      return false;
    }

    // if (!compareDate(selectedDate)) {
    //   return false;
    // }

    if (!clockInChecked && !clockOutChecked) {
      showToast('Pilih jam absen', 'error');
      return false;
    }

    if (clockInChecked && submitData?.clock_in === '') {
      showToast('Jam masuk harus diisi', 'error');
      return false;
    }

    if (clockOutChecked && submitData?.clock_out === '') {
      showToast('Jam pulang harus diisi', 'error');
      return false;
    }

    if (submitData.description === '') {
      showToast('Keterangan harus diisi', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValidate = validateForm();

    if (isValidate) {
      submitData.is_clock_in = requestType === 'clock-in';
      submitData.request_type_code = isPlanned ? 'PLA' : 'UNP';
      submitData.date = selectedDate;

      if (!isPlanned) {
        submitData.unplanned_reason_id = selectedUnplannedOption;
      }

      setIsSubmitting(true);
      await api
        .post('request/attendance', submitData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(() => {
          showToast('Pengajuan absensi berhasil dibuat', 'success');
          document.getElementById('attendance').reset();
          setClockInChecked(false);
          setClockOutChecked(false);
          setSelectedDate('');
          togglePopup();
          refetch();
          setIsSubmitting(false);
        })
        .catch((err) => {
          showToast(`Terjadi kesalahan. "${err.response?.data?.message}"`, 'error');
          setIsSubmitting(false);
        });
    }
  };

  return (
    <Form className="d-flex flex-column gap-4" id="attendance" onSubmit={handleSubmit}>
      <div className="form-group">
        <Label htmlFor="request_type">
          Jenis Pengajuan Untuk{' '}
          <strong> Absen {requestType === 'clock-in' ? 'Masuk' : 'Pulang'}</strong>
        </Label>
        <div className="d-flex gap-3">
          <div className="d-flex gap-2 align-items-center">
            <input
              type="radio"
              name="request_type"
              id="planned"
              defaultChecked={isPlanned}
              onChange={() => setIsPlanned(true)}
            />
            <label htmlFor="planned">Terencana</label>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <input
              type="radio"
              name="request_type"
              id="unplanned"
              defaultChecked={!isPlanned}
              onChange={() => setIsPlanned(false)}
            />
            <label htmlFor="unplanned">Tidak Terencana</label>
          </div>
        </div>
      </div>
      {isPlanned ? (
        <Alert color="info" className="mb-0 mt-0 rounded-4">
          <div className="d-flex gap-2">
            <MaterialIcon icon="info" />
            {requestType === 'clock-in' ? (
              <div className="d-flex flex-column gap-2">
                <span>
                  Pengajuan dilakukan sebelum jam 07:30 WIB karena sudah mengetahui tidak bisa
                  absen/hadir tepat waktu.
                </span>
                <div className="d-flex flex-column bg-light-primary border-1 p-2 rounded-4">
                  <small>Catatan:</small>
                  <span>
                    <i>Silakan absen setelah berada di kantor.</i>
                  </span>
                </div>
              </div>
            ) : (
              <span>
                Pengajuan dilakukan sebelum jam 17:30 WIB karena sudah mengetahui tidak bisa
                melakukan absen pulang/tidak kembali ke kantor.
              </span>
            )}
          </div>
        </Alert>
      ) : (
        <Alert color="primary" className="mb-0 mt-0 rounded-4">
          <div className="d-flex gap-2">
            <MaterialIcon icon="info" />
            {requestType === 'clock-in' ? (
              <div className="d-flex flex-column gap-2">
                <span>Pengajuan setelah jam 07:30 WIB karena situasi tidak terduga.</span>
                <div className="d-flex flex-column bg-light-info border-1 p-2 rounded-4">
                  <small>Catatan:</small>
                  <span>
                    <i>Silakan absen setelah berada di kantor.</i>
                  </span>
                </div>
              </div>
            ) : (
              <span>Pengajuan setelah jam 17:30 WIB karena situasi tidak terduga.</span>
            )}
          </div>
        </Alert>
      )}
      {!isPlanned && (
        <>
          <div className="form-group">
            <Label htmlFor="reason">Alasan Tidak Terencana</Label>
            <select
              className="form-control rounded-4"
              name="reason"
              onChange={(e) => setSelectedUnplannedOption(parseInt(e.target.value, 10))}
              defaultValue=""
            >
              <option value="" selected disabled>
                - Pilih -
              </option>
              {isLoading ? (
                <option value="">Loading...</option>
              ) : error ? (
                <option value="">Error loading options</option>
              ) : (
                unplannedOptions.map((option) =>
                  requestType === 'clock-in' && option.clock_in ? (
                    <option key={option.id} value={option.id}>
                      {option.name} {option.required_attachment && '(Melampirkan bukti)'}
                    </option>
                  ) : requestType === 'clock-out' && option.clock_out ? (
                    <option key={option.id} value={option.id}>
                      {option.name} {option.required_attachment && '(Melampirkan bukti)'}
                    </option>
                  ) : null,
                )
              )}
            </select>
          </div>
          {selectedUplannedMetadData?.required_attachment && (
            <div className="form-group">
              <Label htmlFor="reason">Lampirkan Bukti </Label>
              <div className="d-flex flex-column gap-2">
                <Input
                  type="file"
                  className="rounded-4"
                  name="attachment"
                  onChange={(e) => handleFileChange(e)}
                  required={selectedUplannedMetadData?.required_attachment}
                  accept=".jpg, .jpeg, .png"
                />
                <small className="text-primary">format jpeg/jpg/png | maks. 20MB</small>
              </div>
            </div>
          )}
        </>
      )}
      <div className="form-group">
        <Label htmlFor="reason">Tanggal Absen</Label>
        <input
          type="date"
          name="date"
          className="form-control rounded-4"
          onChange={(e) => setSelectedDate(e.target.value)}
        />
      </div>
      {requestType === 'clock-in' && (
        <div className="d-flex gap-3">
          <div className="form-group w-100">
            <Label htmlFor="clock-in">Absen Masuk</Label>
            <div className="d-flex justify-content-evenly gap-3 align-items-center">
              <input
                type="checkbox"
                name="clock-in-check"
                className="form-check-input form-control rounded-4"
                onChange={(e) => handleCheck(e)}
                disabled={selectedDate === ''}
              />
              <div className="w-25">
                <input
                  type="time"
                  name="clock_in"
                  className="form-control rounded-4"
                  value={clockInChecked ? submitData.clock_in : ''}
                  disabled={!clockInChecked}
                  onChange={(e) => handleChange(e)}
                />
              </div>
              <input
                type="date"
                value={clockInChecked ? selectedDate : ''}
                className="form-control rounded-4"
                readOnly
              />
            </div>
          </div>
        </div>
      )}
      {requestType === 'clock-out' && (
        <div className="d-flex gap-3">
          <div className="form-group w-100">
            <Label htmlFor="clock-in">Absen Pulang</Label>
            <div className="d-flex justify-content-evenly gap-3 align-items-center">
              <input
                type="checkbox"
                name="clock-out-check"
                className="form-check-input form-control rounded-4"
                onChange={(e) => handleCheck(e)}
                disabled={selectedDate === ''}
              />
              <div className="w-25">
                <input
                  type="time"
                  name="clock_out"
                  className="form-control rounded-4"
                  value={clockOutChecked ? submitData.clock_out : ''}
                  disabled={!clockOutChecked}
                  onChange={(e) => handleChange(e)}
                />
              </div>
              <input
                type="date"
                value={clockOutChecked ? selectedDate : ''}
                className="form-control rounded-4"
                readOnly
              />
            </div>
          </div>
        </div>
      )}
      <div className="form-group">
        <Label htmlFor="reason">Keterangan</Label>
        <textarea
          className="form-control rounded-4"
          name="description"
          onChange={(e) => handleChange(e)}
        />
      </div>
      <div className="d-flex justify-content-end gap-2">
        <button type="submit" className="btn btn-primary rounded-4 bg-primary">
          {isSubmitting ? (
            <div className="d-flex gap-2 align-items-center">
              <Spinner size="sm" color="light" />
              <span>Loading...</span>
            </div>
          ) : (
            'Submit'
          )}
        </button>
        <button
          type="submit"
          className="btn btn-outline-secondary rounded-4 text-black"
          disabled={isSubmitting}
          onClick={() => togglePopup()}
        >
          Batal
        </button>
      </div>
    </Form>
  );
};

FormAttendanceReview.propTypes = {
  togglePopup: PropTypes.func,
  refetch: PropTypes.func,
  requestType: PropTypes.string,
};

export default FormAttendanceReview;
